import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  useReducer,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useLocation, useNavigate } from "react-router";
import {
  SnapshotsAPI,
  type SnapshotData,
  ScreenerAPI,
  apiClient,
  DocumentsAPI,
  PortfoliosAPI,
} from "@/services/api";
import { useOsActionsOptional } from "@/components/ui/os-layout/OsContext";
import { useSetWindowTitle } from "@/components/ui/os-layout/useWindow";
import { DocumentsList, type DocumentsListRef } from "@/components/documents/DocumentsList";
import { DashboardProvider } from "@/context/DashboardContext";

import { PageLayout } from "@/components/PageLayout";
import { DestructiveTrashIconButton } from "@/components/ui/DestructiveTrashIconButton";
import { Tooltip } from "@/components/ui/tooltip/Tooltip";
import { UploadDialog } from "@/components/documents/UploadDialog";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  EntityOverview,
  type PortfolioType,
} from "@/components/common/EntityOverview";
import { PortfolioContributions } from "./PortfolioContributions";
import { PortfolioFieldManagement } from "./PortfolioFieldManagement";
import { PortfolioHoldings } from "./PortfolioHoldings";
import { TradeReconciliationDrawer } from "./TradeReconciliationDrawer";
import { PortfolioReports } from "./PortfolioReports";
import { PortfolioXRay } from "./PortfolioXRay";
import {
  buildPlaygroundAssetsFromMetadata,
  formatPlaygroundUrl,
} from "@/utils/playground";
import type { ResourceMetadata } from "@/services/api/screener";
import {
  documentTemplates,
  BLANK_DOCUMENT_TEMPLATE,
} from "@/constants/documentTemplates";
import { useAuth } from "@/context/AuthContext";
import { isAccessDeniedError, ACCESS_DENIED_ERROR, getErrorMessage } from "@/utils/errorUtils";

import {
  faGrid2,
  faUpload,
  faFolder,
  faChartMixed,
  faPieChart,
  faList,
  faArrowsRotate,
  faTrashAlt,
  faFileLines,
  faMagnifyingGlassChart,
  faExpand,
  faCompress,
  faLayerGroup,
  faUserLock,
  faChartLineUp,
  faSpinnerThird,
  faArrowRightArrowLeft,
} from "@awesome.me/kit-70c1f0055f/icons/classic/light";

import { Playground } from "@/components/playground/Playground";
import PlaygroundProvider from "@/context/PlaygroundContext";
import { DocumentEditorDrawer } from "@/components/documents/DocumentEditorDrawer";
import { AniTrackableWidget } from "@/components/AniTrackableWidget";

// UI Components
import {
  Dialog,
  DialogTitle,
  DialogBody,
  DialogActions,
} from "@/components/ui/dialog/Dialog";
import { Button } from "@/components/ui/button/Button";
import { useNotification } from "@/context/NotificationContext";
import { useTaskProgressDialog } from "@/context/TaskProgressDialogContext";

// Import UI components for the holdings table
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
} from "@/components/ui/table/Table";
import { getHoldings, getFullHoldings } from "@/services/api/widget";
import {
  hasNestedPortfolioHoldings,
  hasNestedPortfolioHoldingsInMaps,
} from "./portfolioHoldingsLookthrough";
import { rebalancingApi } from "@/services/api/rebalancing.api";
import DatePicker from "@/components/ui/date-picker";
import { useAniNavTarget } from "@/components/ui/ani-nav";

// Extended HoldingItem interface to support multiple input types
interface HoldingItem {
  id: string;
  name: string;
  weight: number;
  real_weight: number;
  drift: number;
  effective_weight?: number;
  effective_real_weight?: number;
  lookthrough_depth?: number;
  is_portfolio_holding?: boolean;
  is_expanded?: boolean;
  resource_id?: string;
  isin?: string;
  units?: number;
  value?: number;
  product_currency?: string;
  product_currency_value?: number;
  portfolio_currency?: string;
  portfolio_currency_value?: number;
  price_per_unit?: number;
}

const OVERVIEW_HOLDING_CELL_CLASS = "align-top !py-1.5";
const OVERVIEW_NUMERIC_CELL_CLASS = "text-right align-top !py-1.5";
const OVERVIEW_FOOTER_CELL_CLASS = `${OVERVIEW_NUMERIC_CELL_CLASS} text-gray-900 dark:text-gray-100`;

// Define the tabs available in the portfolio interface
export const TABS = {
  OVERVIEW: "overview",
  HOLDINGS: "holdings",
  CONTRIBUTIONS: "contributions",
  XRAY: "xray",
  FIELD_MANAGEMENT: "field_management",
  PLAYGROUND: "playground",
  DOCUMENTS: "documents",
  REPORTS: "reports",
} as const;

type TabId = (typeof TABS)[keyof typeof TABS];

// Define the Portfolio type to match the API response
type LocalPortfolioType = PortfolioType;

// List of playground-specific URL parameters to manage
const PLAYGROUND_URL_PARAMS_TO_MANAGE = [
  "assets",
  "type",
  "period",
  "display",
  "rebase",
  "frequency",
  "statistic",
  "window",
];

// Helper function to extract relevant query parameters from the URL string generated by formatPlaygroundUrl
const extractPlaygroundQueryParams = (
  playgroundUrl: string
): Record<string, string> => {
  const params = new URLSearchParams(playgroundUrl.split("?")[1] || "");
  const relevantParams: Record<string, string> = {};
  PLAYGROUND_URL_PARAMS_TO_MANAGE.forEach((pKey) => {
    if (params.has(pKey)) {
      relevantParams[pKey] = params.get(pKey)!;
    }
  });
  return relevantParams;
};

// Define the state type for portfolio data
type PortfolioState = {
  portfolio: LocalPortfolioType | null;
  metadata: ResourceMetadata | null;
  loading: boolean;
  error: string | null;
  refreshCounter: number;
  /** Indicates the portfolio exists but has no snapshot data yet (newly created) */
  hasNoSnapshots: boolean;
};

// Define the action types
type PortfolioAction =
  | { type: "FETCH_START" }
  | {
      type: "FETCH_SUCCESS";
      payload: { portfolio: LocalPortfolioType; metadata: ResourceMetadata };
    }
  | { type: "FETCH_ERROR"; payload: string }
  | { type: "REFRESH" }
  | { type: "FETCH_NO_SNAPSHOTS"; payload: { metadata: ResourceMetadata } };

// Define the reducer function
const portfolioReducer = (
  state: PortfolioState,
  action: PortfolioAction
): PortfolioState => {
  switch (action.type) {
    case "FETCH_START":
      return { ...state, loading: true, error: null, hasNoSnapshots: false };
    case "FETCH_SUCCESS":
      return {
        ...state,
        loading: false,
        portfolio: action.payload.portfolio,
        metadata: action.payload.metadata,
        hasNoSnapshots: false,
      };
    case "FETCH_ERROR":
      return { ...state, loading: false, error: action.payload, hasNoSnapshots: false };
    case "FETCH_NO_SNAPSHOTS":
      return {
        ...state,
        loading: false,
        portfolio: null,
        metadata: action.payload.metadata,
        error: null,
        hasNoSnapshots: true,
      };
    case "REFRESH":
      return {
        ...state,
        loading: true,
        refreshCounter: state.refreshCounter + 1,
      };
    default:
      return state;
  }
};

// Custom fetch functions for product resources
const fetchPortfolioDocuments = async (
  entityId?: string,
  params?: { tags?: string } & { page?: number; page_size?: number }
) => {
  if (!entityId)
    return {
      results: [],
      pagination: { total: 0, per_page: 0, current_page: 1, last_page: 1 },
    };
  const response = await DocumentsAPI.getDocuments({
    resource_type: "portfolio",
    resource_id: entityId,
    ...params,
  });
  return response;
};

export interface PortfolioComponentProps {
  id: string;
  showHeader?: boolean;
  initialActiveTab?: TabId;
  onPlaygroundClick?: (e: React.MouseEvent) => void;
  /** When true, tab changes will not update the browser URL. Use this when rendered in a drawer/modal. */
  skipUrlNavigation?: boolean;
}

// Portfolio Holdings Only Component
interface PortfolioHoldingsOnlyProps {
  entity: LocalPortfolioType;
  entityId: string;
  onAccessDenied?: () => void;
}

const PortfolioHoldingsOnly: React.FC<PortfolioHoldingsOnlyProps> = ({
  entity,
  entityId,
  onAccessDenied,
}) => {
  // State for holdings data
  const [holdings, setHoldings] = useState<HoldingItem[]>([]);
  const [holdingsLoading, setHoldingsLoading] = useState(false);
  const [holdingsError, setHoldingsError] = useState<string | null>(null);
  const [holdingInputType, setHoldingInputType] =
    useState<string>("weight_based_model");
  const [portfolioClassification, setPortfolioClassification] =
    useState<string>("actual");

  // Trade reconciliation drawer state
  const [tradeDrawerOpen, setTradeDrawerOpen] = useState(false);
  const [selectedTradeHolding, setSelectedTradeHolding] = useState<{
    resourceId: string;
    resourceName: string;
  } | null>(null);

  // State for frontend sorting
  const [sortColumn, setSortColumn] = useState<string>("weight");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  // State for lookthrough functionality
  const [enableLookthrough, setEnableLookthrough] = useState(false);
  const [aggregateMode, setAggregateMode] = useState(false);
  const [hasPortfolioHoldings, setHasPortfolioHoldings] = useState(false);
  const [productIdMaps, setProductIdMaps] = useState<
    Record<
      string,
      {
        product_id: string;
        snapshot_id: string;
        is_portfolio_holding: boolean;
        lookthrough_depth: number;
      }
    >
  >({});
  const [productInfoMap, setProductInfoMap] = useState<
    Record<string, { isin?: string; currency?: string }>
  >({});

  // State for target comparison toggle (actual portfolios with TARGET holdings)
  const [hasTargetHoldings, setHasTargetHoldings] = useState(false);
  const [showTargetComparison, setShowTargetComparison] = useState(false);

  // State for historical date selection
  const [selectedAsAt, setSelectedAsAt] = useState<string>(
    entity.metadata.as_at.split("T")[0] // Convert to YYYY-MM-DD format
  );

  // Ref to prevent duplicate fetches
  const isFetchingHoldings = useRef(false);
  // Apply lookthrough + aggregate defaults once per portfolio (portfolios of portfolios)
  const lookthroughDefaultsAppliedRef = useRef(false);
  const prevLookthroughEntityIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (prevLookthroughEntityIdRef.current === entityId) {
      return;
    }
    prevLookthroughEntityIdRef.current = entityId;
    lookthroughDefaultsAppliedRef.current = false;
    setEnableLookthrough(false);
    setAggregateMode(false);
    setHasPortfolioHoldings(false);
    setHoldings([]);
    setHoldingsError(null);
    setHoldingsLoading(true);
  }, [entityId]);

  // Backfill ISIN / currency for holding subtitles (some holdings don't carry these)
  useEffect(() => {
    const ids = new Set<string>();
    holdings.forEach((h) => {
      if (!h.resource_id) return;
      if (!h.isin || !h.product_currency) ids.add(h.resource_id);
    });

    const missing = Array.from(ids).filter((id) => !productInfoMap[id]);
    if (missing.length === 0) return;

    let cancelled = false;
    (async () => {
      const info = await rebalancingApi.lookupProductInfo(missing);
      if (cancelled || !info) return;
      setProductInfoMap((prev) => {
        const next = { ...prev };
        Object.entries(info).forEach(([pid, v]) => {
          next[pid] = { isin: v?.isin, currency: v?.currency };
        });
        return next;
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [holdings]);

  const getHoldingMetaLine = useCallback(
    (holding: HoldingItem): string | null => {
      const fallback = holding.resource_id
        ? productInfoMap[holding.resource_id]
        : undefined;
      const isin = holding.isin || fallback?.isin;
      const currency = holding.product_currency || fallback?.currency;
      const parts = [isin, currency].filter(Boolean);
      return parts.length > 0 ? parts.join(" · ") : null;
    },
    [productInfoMap]
  );

  // Navigation hooks
  const navigate = useNavigate();
  const osContext = useOsActionsOptional();

  // Handle date change
  const handleDateChange = (date: string) => {
    setSelectedAsAt(date);
  };

  // Handle row click for navigation
  const handleRowClick = (holding: HoldingItem) => {
    if (!holding.resource_id) return;

    // If in OS mode, open in a new window
    if (osContext) {
      if (holding.is_portfolio_holding) {
        osContext.openWindow({
          appId: 'portfolio',
          title: holding.name || 'Portfolio',
          params: { id: holding.resource_id },
        });
      } else {
        osContext.openWindow({
          appId: 'product',
          title: holding.name || 'Product',
          params: { id: holding.resource_id },
        });
      }
    } else {
      // Classic layout - use React Router navigation
      if (holding.is_portfolio_holding) {
        navigate(`/portfolio/${holding.resource_id}`);
      } else {
        navigate(`/product/${holding.resource_id}`);
      }
    }
  };

  // Handle opening trade reconciliation drawer
  const handleOpenTradeDrawer = (holding: HoldingItem, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!holding.resource_id) return;
    setSelectedTradeHolding({
      resourceId: holding.resource_id,
      resourceName: holding.name,
    });
    setTradeDrawerOpen(true);
  };

  // Handle column sorting
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("desc");
    }
  };

  // Sort holdings based on current sort state
  const sortedHoldings = useMemo(() => {
    if (!holdings) return [];

    // If lookthrough is disabled or we don't have any expanded holdings, use simple sort
    // Also use simple sort for aggregated data since it's already flattened
    const hasExpandedHoldings = holdings.some(
      (h) => (h.lookthrough_depth || 0) > 0
    );

    if (!enableLookthrough || !hasExpandedHoldings || aggregateMode) {
      const sorted = [...holdings].sort((a, b) => {
        let aVal = a[sortColumn as keyof HoldingItem];
        let bVal = b[sortColumn as keyof HoldingItem];

        // Handle undefined/null values
        if (aVal === undefined || aVal === null) aVal = 0;
        if (bVal === undefined || bVal === null) bVal = 0;

        // Convert to numbers for numeric columns
        if (typeof aVal === "number" && typeof bVal === "number") {
          return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
        }

        // String comparison for name
        if (typeof aVal === "string" && typeof bVal === "string") {
          return sortDirection === "asc"
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        }

        return 0;
      });

      return sorted;
    }

    // For lookthrough mode, we need hierarchical sorting
    const result: HoldingItem[] = [];
    const processed = new Set<string>();

    // First, collect and sort all top-level holdings (depth 0)
    const topLevel = holdings.filter((h) => (h.lookthrough_depth || 0) === 0);
    topLevel.sort((a, b) => {
      let aVal = a[sortColumn as keyof HoldingItem];
      let bVal = b[sortColumn as keyof HoldingItem];

      if (aVal === undefined || aVal === null) aVal = 0;
      if (bVal === undefined || bVal === null) bVal = 0;

      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
      }

      if (typeof aVal === "string" && typeof bVal === "string") {
        return sortDirection === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      return 0;
    });

    // Process each top-level holding and its children
    for (const parent of topLevel) {
      result.push(parent);
      processed.add(parent.id);

      // Find the index of this parent in the original holdings array
      const parentIndex = holdings.findIndex((h) => h.id === parent.id);

      // Collect children that follow this parent (until we hit another top-level item)
      const children: HoldingItem[] = [];
      for (let i = parentIndex + 1; i < holdings.length; i++) {
        const holding = holdings[i];
        if ((holding.lookthrough_depth || 0) === 0) break; // Hit another parent
        if (
          (holding.lookthrough_depth || 0) > 0 &&
          !processed.has(holding.id)
        ) {
          children.push(holding);
          processed.add(holding.id);
        }
      }

      // Sort children within this parent group
      children.sort((a, b) => {
        let aVal = a[sortColumn as keyof HoldingItem];
        let bVal = b[sortColumn as keyof HoldingItem];

        if (aVal === undefined || aVal === null) aVal = 0;
        if (bVal === undefined || bVal === null) bVal = 0;

        if (typeof aVal === "number" && typeof bVal === "number") {
          return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
        }

        if (typeof aVal === "string" && typeof bVal === "string") {
          return sortDirection === "asc"
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        }

        return 0;
      });

      // Add sorted children to result
      result.push(...children);
    }

    return result;
  }, [holdings, sortColumn, sortDirection, enableLookthrough, aggregateMode]);

  // Aggregate holdings for target comparison view
  // When the same holding appears multiple times, combine them:
  // - Sum current weights
  // - Keep target weight only once (avoid double counting)
  // - Recalculate drift from aggregated values
  const aggregatedForTargetComparison = useMemo(() => {
    if (!showTargetComparison || !sortedHoldings.length) return sortedHoldings;

    const aggregatedMap = new Map<
      string,
      HoldingItem & { _originalCount: number }
    >();

    for (const holding of sortedHoldings) {
      // Use resource_id as key, fall back to name if not available
      const key = holding.resource_id || holding.name;

      if (aggregatedMap.has(key)) {
        const existing = aggregatedMap.get(key)!;
        // Sum current weights
        existing.weight = (existing.weight || 0) + (holding.weight || 0);
        // Sum portfolio values
        existing.portfolio_currency_value =
          (existing.portfolio_currency_value || 0) +
          (holding.portfolio_currency_value || 0);
        existing.product_currency_value =
          (existing.product_currency_value || 0) +
          (holding.product_currency_value || 0);
        existing.units = (existing.units || 0) + (holding.units || 0);
        // Keep real_weight (target) as-is - don't double count
        // Recalculate drift based on aggregated current vs single target
        existing.drift = (existing.weight || 0) - (existing.real_weight || 0);
        existing._originalCount += 1;
      } else {
        aggregatedMap.set(key, {
          ...holding,
          // Recalculate drift in case it wasn't set correctly
          drift: (holding.weight || 0) - (holding.real_weight || 0),
          _originalCount: 1,
        });
      }
    }

    // Convert back to array and sort by weight descending
    const aggregated = Array.from(aggregatedMap.values());
    aggregated.sort((a, b) => {
      let aVal = a[sortColumn as keyof HoldingItem];
      let bVal = b[sortColumn as keyof HoldingItem];

      if (aVal === undefined || aVal === null) aVal = 0;
      if (bVal === undefined || bVal === null) bVal = 0;

      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
      }

      if (typeof aVal === "string" && typeof bVal === "string") {
        return sortDirection === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      return 0;
    });

    return aggregated;
  }, [sortedHoldings, showTargetComparison, sortColumn, sortDirection]);

  // Use aggregated holdings when in target comparison mode, otherwise use sorted
  const displayHoldings = showTargetComparison
    ? aggregatedForTargetComparison
    : sortedHoldings;

  // Calculate totals for the footer row
  const totals = useMemo(() => {
    if (!holdings || holdings.length === 0) return null;

    // When in target comparison mode, use aggregated holdings for correct totals
    // When lookthrough is enabled but not aggregated, only sum top-level holdings to avoid double counting
    // When aggregated, sum all holdings since duplicates have been combined
    let holdingsToSum: HoldingItem[];

    if (showTargetComparison && aggregatedForTargetComparison.length > 0) {
      // Use aggregated holdings for target comparison totals
      holdingsToSum = aggregatedForTargetComparison;
    } else if (enableLookthrough && !aggregateMode) {
      holdingsToSum = holdings.filter((h) => (h.lookthrough_depth || 0) === 0);
    } else {
      holdingsToSum = holdings;
    }

    const weightSum = holdingsToSum.reduce(
      (sum, h) => sum + (typeof h.weight === "number" ? h.weight : 0),
      0
    );
    const realWeightSum = holdingsToSum.reduce(
      (sum, h) => sum + (typeof h.real_weight === "number" ? h.real_weight : 0),
      0
    );
    const effectiveWeightSum = holdingsToSum.reduce(
      (sum, h) =>
        sum + (typeof h.effective_weight === "number" ? h.effective_weight : 0),
      0
    );
    const effectiveRealWeightSum = holdingsToSum.reduce(
      (sum, h) =>
        sum +
        (typeof h.effective_real_weight === "number"
          ? h.effective_real_weight
          : 0),
      0
    );

    // Calculate portfolio value total
    const portfolioValueSum = holdingsToSum.reduce(
      (sum, h) =>
        sum +
        (typeof h.portfolio_currency_value === "number"
          ? h.portfolio_currency_value
          : 0),
      0
    );

    return {
      weight: weightSum,
      real_weight: realWeightSum,
      effective_weight: effectiveWeightSum,
      effective_real_weight: effectiveRealWeightSum,
      portfolio_value: portfolioValueSum,
      // Calculate drift as the difference between current weight and target weight
      // For target comparison: current - target (positive = over target)
      drift: enableLookthrough
        ? effectiveWeightSum - effectiveRealWeightSum
        : weightSum - realWeightSum,
    };
  }, [
    holdings,
    enableLookthrough,
    aggregateMode,
    showTargetComparison,
    aggregatedForTargetComparison,
  ]);

  // Fetch holdings data
  useEffect(() => {
    async function fetchHoldingsData() {
      if (!entityId || isFetchingHoldings.current) return;

      let keepLoadingAfterFetch = false;

      try {
        isFetchingHoldings.current = true;
        setHoldingsLoading(true);
        setHoldingsError(null);

        // Try getFullHoldings to get the complete response with metadata
        const fullResponse = await getFullHoldings({
          resources: [
            {
              resource_type: "portfolio",
              resource_id: entityId,
            },
          ],
          as_at: selectedAsAt,
          columns: [
            "name",
            "weight",
            "real_weight",
            "drift",
            "effective_weight",
            "effective_real_weight",
            "lookthrough_depth",
            "is_portfolio_holding",
            "units",
            "value",
            "product_currency",
            "product_currency_value",
            "portfolio_currency",
            "portfolio_currency_value",
            "price_per_unit",
          ],
          sort_by: "weight",
          sort_direction: "desc",
          enable_lookthrough: enableLookthrough,
          max_lookthrough_depth: 1,
          aggregate_lookthrough: aggregateMode,
        });

        const resourceId = `portfolio:${entityId}`;

        // The fullResponse IS the complete response with data and metadata
        const holdingsData = fullResponse as any;

        // Debug: Log the response structure
        console.log("Holdings API Response:", holdingsData);
        console.log("Response type:", typeof holdingsData);
        console.log("Response keys:", Object.keys(holdingsData));

        // Get holding_input_type and has_target_holdings from metadata
        if (holdingsData.metadata?.holding_input_type) {
          const inputType = holdingsData.metadata.holding_input_type;
          setHoldingInputType(inputType);

          // If we have units_based input type, this is definitely an actual portfolio
          if (
            inputType === "units_based" ||
            inputType === "value_based" ||
            inputType === "value_product_currency_based"
          ) {
            console.log(
              "Detected actual portfolio from input type:",
              inputType
            );
            setPortfolioClassification("actual");
          }
        }

        // Check if TARGET holdings exist for this portfolio
        if (holdingsData.metadata?.has_target_holdings !== undefined) {
          setHasTargetHoldings(holdingsData.metadata.has_target_holdings);
          console.log(
            "Has TARGET holdings:",
            holdingsData.metadata.has_target_holdings
          );
        }

        if (!holdingsData.metadata?.holding_input_type) {
          // Try to get from portfolio holdings collection directly
          try {
            const holdingsResponse = await apiClient.get(
              `/portfolios/${entityId}/holdings/all?page=1&page_size=1`
            );
            if (holdingsResponse.data.data.length > 0) {
              const latestHoldingRecord = holdingsResponse.data.data[0];
              if (
                latestHoldingRecord.holdings &&
                latestHoldingRecord.holdings.length > 0
              ) {
                const firstHolding = latestHoldingRecord.holdings[0];
                if (firstHolding.calculated_amounts?.input_method) {
                  // Map input_method to our holding_input_type format
                  const inputMethod =
                    firstHolding.calculated_amounts.input_method;
                  if (inputMethod === "units") {
                    setHoldingInputType("units_based");
                  } else if (inputMethod === "value_product_currency") {
                    setHoldingInputType("value_product_currency_based");
                  } else if (inputMethod === "value_portfolio_currency") {
                    setHoldingInputType("value_based");
                  } else {
                    setHoldingInputType("weight_based_model");
                  }
                } else {
                  setHoldingInputType(
                    latestHoldingRecord.holding_input_type ||
                      "weight_based_model"
                  );
                }
              }
            }
          } catch (error) {
            console.warn("Could not fetch holding input type:", error);
          }
        }

        // Fetch portfolio classification
        try {
          const portfolioResponse = await apiClient.get(
            `/portfolios/${entityId}`
          );
          console.log("Portfolio API response:", portfolioResponse.data);
          if (
            portfolioResponse.data &&
            portfolioResponse.data.portfolio_classification
          ) {
            console.log(
              "Setting portfolioClassification to:",
              portfolioResponse.data.portfolio_classification
            );
            setPortfolioClassification(
              portfolioResponse.data.portfolio_classification
            );
          } else {
            console.log("No portfolio_classification found, keeping as actual");
            // Keep as actual since we know this portfolio has units/values
          }
        } catch (error) {
          console.error("Could not fetch portfolio classification:", error);
        }

        // Try both structures - direct access and via data property
        let resourceData =
          holdingsData[resourceId] || holdingsData.data?.[resourceId];
        let productMaps =
          holdingsData.metadata?.product_id_maps?.[resourceId] || {};

        // If exact key doesn't exist, try to find a matching key
        if (!resourceData) {
          // Try direct access first
          if (holdingsData && typeof holdingsData === "object") {
            const dataKeys = Object.keys(holdingsData);
            const matchingKey = dataKeys.find((key) => key.includes(entityId));
            if (matchingKey) {
              resourceData = holdingsData[matchingKey];
              productMaps =
                holdingsData.metadata?.product_id_maps?.[matchingKey] || {};
            }
          }

          // If still not found, try via data property
          if (!resourceData && holdingsData.data) {
            const dataKeys = Object.keys(holdingsData.data);
            const matchingKey = dataKeys.find((key) => key.includes(entityId));
            if (matchingKey) {
              resourceData = holdingsData.data[matchingKey];
              productMaps =
                holdingsData.metadata?.product_id_maps?.[matchingKey] || {};
            }
          }
        }

        if (!resourceData) {
          setHoldingsError("No holdings data found for this portfolio");
          return;
        }
        setProductIdMaps(productMaps);

        if (
          !lookthroughDefaultsAppliedRef.current &&
          !enableLookthrough &&
          !aggregateMode
        ) {
          const hasNestedPortfolios =
            hasNestedPortfolioHoldingsInMaps(productMaps);
          if (hasNestedPortfolios) {
            lookthroughDefaultsAppliedRef.current = true;
            setHasPortfolioHoldings(true);
            setEnableLookthrough(true);
            setAggregateMode(true);
            keepLoadingAfterFetch = true;
            return;
          }
          lookthroughDefaultsAppliedRef.current = true;
        }

        if (
          resourceData &&
          Array.isArray(resourceData.cols) &&
          Array.isArray(resourceData.rows)
        ) {
          const nameColIndex = resourceData.cols.findIndex(
            (col: { id: string }) => col.id === "name"
          );
          const weightColIndex = resourceData.cols.findIndex(
            (col: { id: string }) => col.id === "weight"
          );
          const realWeightColIndex = resourceData.cols.findIndex(
            (col: { id: string }) => col.id === "real_weight"
          );
          const driftColIndex = resourceData.cols.findIndex(
            (col: { id: string }) => col.id === "drift"
          );
          const effectiveWeightColIndex = resourceData.cols.findIndex(
            (col: { id: string }) => col.id === "effective_weight"
          );
          const effectiveRealWeightColIndex = resourceData.cols.findIndex(
            (col: { id: string }) => col.id === "effective_real_weight"
          );
          const lookthoughDepthColIndex = resourceData.cols.findIndex(
            (col: { id: string }) => col.id === "lookthrough_depth"
          );
          const isPortfolioHoldingColIndex = resourceData.cols.findIndex(
            (col: { id: string }) => col.id === "is_portfolio_holding"
          );
          const unitsColIndex = resourceData.cols.findIndex(
            (col: { id: string }) => col.id === "units"
          );
          const valueColIndex = resourceData.cols.findIndex(
            (col: { id: string }) => col.id === "value"
          );
          const productCurrencyColIndex = resourceData.cols.findIndex(
            (col: { id: string }) => col.id === "product_currency"
          );
          const productCurrencyValueColIndex = resourceData.cols.findIndex(
            (col: { id: string }) => col.id === "product_currency_value"
          );
          const portfolioCurrencyColIndex = resourceData.cols.findIndex(
            (col: { id: string }) => col.id === "portfolio_currency"
          );
          const portfolioCurrencyValueColIndex = resourceData.cols.findIndex(
            (col: { id: string }) => col.id === "portfolio_currency_value"
          );
          const pricePerUnitColIndex = resourceData.cols.findIndex(
            (col: { id: string }) => col.id === "price_per_unit"
          );

          if (
            nameColIndex !== -1 &&
            weightColIndex !== -1 &&
            realWeightColIndex !== -1 &&
            driftColIndex !== -1
          ) {
            const transformedHoldings = resourceData.rows
              .map((row: any[], index: number) => ({
                id: `holding-${index}`,
                name: row[nameColIndex] as string,
                weight: row[weightColIndex] as number,
                real_weight: row[realWeightColIndex] as number,
                drift: row[driftColIndex] as number,
                effective_weight:
                  effectiveWeightColIndex !== -1
                    ? (row[effectiveWeightColIndex] as number)
                    : undefined,
                effective_real_weight:
                  effectiveRealWeightColIndex !== -1
                    ? (row[effectiveRealWeightColIndex] as number)
                    : undefined,
                lookthrough_depth:
                  lookthoughDepthColIndex !== -1
                    ? (row[lookthoughDepthColIndex] as number)
                    : 0,
                is_portfolio_holding:
                  isPortfolioHoldingColIndex !== -1
                    ? row[isPortfolioHoldingColIndex] === "Yes"
                    : false,
                is_expanded:
                  lookthoughDepthColIndex !== -1
                    ? (row[lookthoughDepthColIndex] as number) > 0
                    : false,
                resource_id: (productMaps as any)[`row_${index}`]?.product_id,
                // Get values from API columns (backend now extracts from calculated_amounts)
                units:
                  unitsColIndex !== -1
                    ? (row[unitsColIndex] as number)
                    : undefined,
                value:
                  valueColIndex !== -1
                    ? (row[valueColIndex] as number)
                    : undefined,
                product_currency:
                  productCurrencyColIndex !== -1
                    ? (row[productCurrencyColIndex] as string)
                    : undefined,
                product_currency_value:
                  productCurrencyValueColIndex !== -1
                    ? (row[productCurrencyValueColIndex] as number)
                    : undefined,
                portfolio_currency:
                  portfolioCurrencyColIndex !== -1
                    ? (row[portfolioCurrencyColIndex] as string)
                    : "USD",
                portfolio_currency_value:
                  portfolioCurrencyValueColIndex !== -1
                    ? (row[portfolioCurrencyValueColIndex] as number)
                    : undefined,
                price_per_unit:
                  pricePerUnitColIndex !== -1
                    ? (row[pricePerUnitColIndex] as number)
                    : undefined,
              }))
              .filter(
                (item: HoldingItem | null): item is HoldingItem => item !== null
              );

            setHoldings(transformedHoldings);

            setHasPortfolioHoldings((prev) =>
              hasNestedPortfolioHoldings(productMaps, transformedHoldings) ||
              prev
            );
          } else {
            setHoldingsError("Required column data not found");
          }
        } else {
          setHoldings([]);
        }
      } catch (error: any) {
        // Check for 403 access denied errors using unified utility
        const { isAccessDenied } = isAccessDeniedError(error);

        if (isAccessDenied) {
          setHoldingsError(ACCESS_DENIED_ERROR);
          // Notify parent component so it can show full page access denied
          if (onAccessDenied) {
            onAccessDenied();
          }
        } else {
          const errorMessage = error instanceof Error ? error.message : String(error);
          setHoldingsError(errorMessage || "Failed to fetch holdings data");
        }
      } finally {
        if (!keepLoadingAfterFetch) {
          setHoldingsLoading(false);
        }
        isFetchingHoldings.current = false;
      }
    }

    fetchHoldingsData();
  }, [selectedAsAt, entityId, enableLookthrough, aggregateMode]);

  const holdingsNavRef = useAniNavTarget<HTMLDivElement>({ id: "holdings", label: "Holdings" });
  const asAtNavRef = useAniNavTarget<HTMLDivElement>({ id: "as_at", label: "As at date" });
  const lookthroughNavRef = useAniNavTarget<HTMLButtonElement>({
    id: "lookthrough",
    label: "Lookthrough",
    kind: "action",
    enabled: hasPortfolioHoldings,
  });

  // Show Access Denied UI for 403 errors
  if (holdingsError === ACCESS_DENIED_ERROR) {
    return (
      <div className="h-full flex items-center justify-center p-8">
        <div className="text-center max-w-md">
          <div className="mx-auto w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m0 0v2m0-2h2m-2 0H10m4-6V5a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2h2m8 0a2 2 0 012 2v6a2 2 0 01-2 2H8a2 2 0 01-2-2v-6a2 2 0 012-2h8z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Access Denied
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            You don't have permission to view this portfolio's holdings. Please contact your administrator if you believe this is an error.
          </p>
          <button
            onClick={() => {
              if (osContext) {
                osContext.openWindow({
                  appId: "screener",
                  title: "Screener",
                  params: {},
                });
              } else {
                navigate("/screener");
              }
            }}
            className="inline-flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
          >
            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Screener
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full">
      <div className="px-4">
        <div ref={holdingsNavRef} className="px-2 py-6">
          <div className="flex flex-col gap-3 text-gray-900 dark:text-gray-100">
            <div className="flex items-center justify-between">
              <span className="text-lg font-medium">Holdings</span>
              <div className="flex items-center gap-3">
                {hasPortfolioHoldings && (
                  <button
                    ref={lookthroughNavRef}
                    onClick={() => {
                      const newLookthrough = !enableLookthrough;
                      setEnableLookthrough(newLookthrough);
                      // Reset aggregate mode when turning off lookthrough
                      if (!newLookthrough) {
                        setAggregateMode(false);
                      }
                    }}
                    className="flex items-center gap-2 px-3 py-1 text-sm bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-md transition-colors"
                    title={
                      enableLookthrough
                        ? "Hide underlying holdings"
                        : "Show underlying holdings"
                    }
                  >
                    <FontAwesomeIcon
                      icon={enableLookthrough ? faCompress : faExpand}
                      className="text-xs"
                    />
                    {enableLookthrough ? "Collapse" : "Lookthrough"}
                  </button>
                )}
                {enableLookthrough && hasPortfolioHoldings && (
                  <button
                    onClick={() => setAggregateMode(!aggregateMode)}
                    className={`flex items-center gap-2 px-3 py-1 text-sm rounded-md transition-colors ${
                      aggregateMode
                        ? "bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200"
                        : "bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700"
                    }`}
                    title={
                      aggregateMode
                        ? "Show hierarchical breakdown"
                        : "Combine duplicate holdings"
                    }
                  >
                    <FontAwesomeIcon icon={faLayerGroup} className="text-xs" />
                    Aggregate
                  </button>
                )}
                {/* Target comparison toggle for actual portfolios with TARGET holdings */}
                {portfolioClassification === "actual" && hasTargetHoldings && (
                  <button
                    onClick={() =>
                      setShowTargetComparison(!showTargetComparison)
                    }
                    className={`flex items-center gap-2 px-3 py-1 text-sm rounded-md transition-colors ${
                      showTargetComparison
                        ? "bg-emerald-100 dark:bg-emerald-900 text-emerald-800 dark:text-emerald-200"
                        : "bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700"
                    }`}
                    title={
                      showTargetComparison
                        ? "Show value breakdown"
                        : "Compare to target weights"
                    }
                  >
                    <FontAwesomeIcon icon={faChartMixed} className="text-xs" />
                    {showTargetComparison ? "Hide Targets" : "vs Target"}
                  </button>
                )}
              </div>
            </div>
            {/* Date Picker for historical data */}
            <div ref={asAtNavRef} className="flex items-center gap-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                As at:
              </span>
              <div className="relative">
                <DatePicker
                  value={selectedAsAt}
                  onChange={(date) => handleDateChange(date as string)}
                  mode="single"
                  disableFutureDates={true}
                  disableCalendar={false}
                  placeholder="Select date"
                  showPresets={false}
                  className="w-48"
                />
              </div>
            </div>
          </div>
          {holdingsLoading ? (
            <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
              Loading holdings for {selectedAsAt}...
            </p>
          ) : holdingsError ? (
            <p className="mt-3 text-sm text-red-500 dark:text-red-400">
              Error: {holdingsError}
            </p>
          ) : holdings?.length > 0 ? (
            <>
              {/* Show the date being displayed */}
              <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Showing holdings as at {selectedAsAt}
                {selectedAsAt !== entity.metadata.as_at.split("T")[0] && (
                  <span className="ml-2 text-amber-600 dark:text-amber-400">
                    (Historical data)
                  </span>
                )}
                {aggregateMode && (
                  <span className="ml-2 px-2 py-0.5 text-xs bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200 rounded">
                    Aggregated View
                  </span>
                )}
              </div>
              <div className="mt-4">
                <Table dense striped>
                  <TableHead>
                    <TableRow>
                      <TableHeader
                        className="w-2/5 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => handleSort("name")}
                      >
                        <div className="flex items-center gap-1">
                          Holding
                          {sortColumn === "name" && (
                            <span className="text-xs">
                              {sortDirection === "asc" ? "↑" : "↓"}
                            </span>
                          )}
                        </div>
                      </TableHeader>

                      {/* Show different columns for actual portfolios */}
                      {/* Value columns - show when NOT in target comparison mode */}
                      {portfolioClassification === "actual" &&
                        !showTargetComparison && (
                          <>
                            <TableHeader className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700">
                              <div className="flex items-center justify-end gap-1">
                                Units
                              </div>
                            </TableHeader>
                            <TableHeader className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700">
                              <div className="flex items-center justify-end gap-1">
                                Price
                              </div>
                            </TableHeader>
                            <TableHeader className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700">
                              <div className="flex items-center justify-end gap-1">
                                Product Value
                              </div>
                            </TableHeader>
                            <TableHeader className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700">
                              <div className="flex items-center justify-end gap-1">
                                Portfolio Value
                              </div>
                            </TableHeader>
                          </>
                        )}

                      {/* Target comparison columns - show when in target comparison mode */}
                      {portfolioClassification === "actual" &&
                        showTargetComparison && (
                          <>
                            <TableHeader
                              className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                              onClick={() => handleSort("real_weight")}
                            >
                              <div className="flex items-center justify-end gap-1">
                                Target Weight (%)
                                {sortColumn === "real_weight" && (
                                  <span className="text-xs">
                                    {sortDirection === "asc" ? "↑" : "↓"}
                                  </span>
                                )}
                              </div>
                            </TableHeader>
                            <TableHeader
                              className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                              onClick={() => handleSort("weight")}
                            >
                              <div className="flex items-center justify-end gap-1">
                                Current Weight (%)
                                {sortColumn === "weight" && (
                                  <span className="text-xs">
                                    {sortDirection === "asc" ? "↑" : "↓"}
                                  </span>
                                )}
                              </div>
                            </TableHeader>
                            <TableHeader
                              className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                              onClick={() => handleSort("drift")}
                            >
                              <div className="flex items-center justify-end gap-1">
                                Difference (%)
                                {sortColumn === "drift" && (
                                  <span className="text-xs">
                                    {sortDirection === "asc" ? "↑" : "↓"}
                                  </span>
                                )}
                              </div>
                            </TableHeader>
                          </>
                        )}

                      {/* Model portfolios: Show both model and actual weights + drift */}
                      {holdingInputType === "weight_based_model" &&
                        portfolioClassification === "model" && (
                          <>
                            <TableHeader
                              className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                              onClick={() =>
                                handleSort(
                                  enableLookthrough
                                    ? "effective_real_weight"
                                    : "real_weight"
                                )
                              }
                            >
                              <div className="flex items-center justify-end gap-1">
                                {enableLookthrough
                                  ? "Effective Model Weight (%)"
                                  : "Model Weight (%)"}
                                {sortColumn ===
                                  (enableLookthrough
                                    ? "effective_real_weight"
                                    : "real_weight") && (
                                  <span className="text-xs">
                                    {sortDirection === "asc" ? "↑" : "↓"}
                                  </span>
                                )}
                              </div>
                            </TableHeader>

                            <TableHeader
                              className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                              onClick={() =>
                                handleSort(
                                  enableLookthrough
                                    ? "effective_weight"
                                    : "weight"
                                )
                              }
                            >
                              <div className="flex items-center justify-end gap-1">
                                {enableLookthrough
                                  ? "Effective Actual Weight (%)"
                                  : "Actual Weight (%)"}
                                {sortColumn ===
                                  (enableLookthrough
                                    ? "effective_weight"
                                    : "weight") && (
                                  <span className="text-xs">
                                    {sortDirection === "asc" ? "↑" : "↓"}
                                  </span>
                                )}
                              </div>
                            </TableHeader>

                            <TableHeader
                              className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                              onClick={() => handleSort("drift")}
                            >
                              <div className="flex items-center justify-end gap-1">
                                Drift (%)
                                {sortColumn === "drift" && (
                                  <span className="text-xs">
                                    {sortDirection === "asc" ? "↑" : "↓"}
                                  </span>
                                )}
                              </div>
                            </TableHeader>
                          </>
                        )}

                      {/* Actual portfolios: Show only current weight (when NOT in target comparison mode) */}
                      {portfolioClassification === "actual" &&
                        !showTargetComparison && (
                          <TableHeader
                            className="text-right cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                            onClick={() =>
                              handleSort(
                                enableLookthrough
                                  ? "effective_weight"
                                  : "weight"
                              )
                            }
                          >
                            <div className="flex items-center justify-end gap-1">
                              {enableLookthrough
                                ? "Effective Weight (%)"
                                : "Current Weight (%)"}
                              {sortColumn ===
                                (enableLookthrough
                                  ? "effective_weight"
                                  : "weight") && (
                                <span className="text-xs">
                                  {sortDirection === "asc" ? "↑" : "↓"}
                                </span>
                              )}
                            </div>
                          </TableHeader>
                        )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayHoldings.map((holding) => {
                      const holdingMetaLine = getHoldingMetaLine(holding);
                      return (
                      <TableRow
                        key={holding.id}
                        className="hover:bg-gray-100 dark:hover:bg-gray-700/50"
                      >
                        <TableCell className={OVERVIEW_HOLDING_CELL_CLASS}>
                          <div
                            style={
                              enableLookthrough &&
                              holding.lookthrough_depth !== undefined &&
                              holding.lookthrough_depth > 0
                                ? {
                                    marginLeft: `${
                                      holding.lookthrough_depth * 12
                                    }px`,
                                  }
                                : undefined
                            }
                          >
                            <div className="flex items-start min-w-0">
                              {enableLookthrough &&
                                holding.lookthrough_depth !== undefined &&
                                holding.lookthrough_depth > 0 && (
                                  <span className="mr-2 text-xs text-gray-400 flex-shrink-0 leading-tight">
                                    ↳
                                  </span>
                                )}
                              <div className="min-w-0 leading-tight">
                                <div className="flex items-baseline min-w-0 gap-1">
                                  <span
                                    className="truncate inline-block max-w-[300px] hover:text-indigo-500 hover:underline cursor-pointer transition-colors duration-150 font-medium text-gray-900 dark:text-white"
                                    title={holding.name}
                                    onClick={() => handleRowClick(holding)}
                                  >
                                    {holding.name}
                                  </span>
                                  {holding.is_portfolio_holding && (
                                    <span className="px-1 py-0 text-[10px] leading-none bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded flex-shrink-0">
                                      Portfolio
                                    </span>
                                  )}
                                  {holding.resource_id && (
                                    <button
                                      onClick={(e) =>
                                        handleOpenTradeDrawer(holding, e)
                                      }
                                      className="inline-flex h-4 w-4 shrink-0 items-center justify-center text-slate-400 hover:text-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded transition-colors"
                                      title="View trade reconciliation"
                                    >
                                      <FontAwesomeIcon
                                        icon={faArrowRightArrowLeft}
                                        className="text-[10px]"
                                      />
                                    </button>
                                  )}
                                </div>
                                {holdingMetaLine && (
                                  <div className="text-[11px] leading-tight text-gray-500 dark:text-gray-400 truncate mt-0.5">
                                    {holdingMetaLine}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </TableCell>

                        {/* Show different columns for actual portfolios */}
                        {/* Value columns - show when NOT in target comparison mode */}
                        {portfolioClassification === "actual" &&
                          !showTargetComparison && (
                            <>
                              <TableCell className={OVERVIEW_NUMERIC_CELL_CLASS}>
                                {holding.units
                                  ? holding.units.toLocaleString()
                                  : "-"}
                              </TableCell>
                              <TableCell className={OVERVIEW_NUMERIC_CELL_CLASS}>
                                {holding.price_per_unit
                                  ? holding.price_per_unit.toLocaleString(
                                      undefined,
                                      {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 4,
                                      }
                                    )
                                  : "-"}
                              </TableCell>
                              <TableCell className={OVERVIEW_NUMERIC_CELL_CLASS}>
                                {holding.product_currency_value
                                  ? holding.product_currency_value.toLocaleString(
                                      undefined,
                                      { maximumFractionDigits: 0 }
                                    )
                                  : "-"}
                              </TableCell>
                              <TableCell className={OVERVIEW_NUMERIC_CELL_CLASS}>
                                {holding.portfolio_currency_value
                                  ? holding.portfolio_currency_value.toLocaleString(
                                      undefined,
                                      { maximumFractionDigits: 0 }
                                    )
                                  : "-"}
                              </TableCell>
                            </>
                          )}

                        {/* Target comparison columns - show when in target comparison mode */}
                        {portfolioClassification === "actual" &&
                          showTargetComparison && (
                            <>
                              <TableCell className={OVERVIEW_NUMERIC_CELL_CLASS}>
                                {holding.real_weight
                                  ? Number(holding.real_weight).toFixed(2)
                                  : "0.00"}
                                %
                              </TableCell>
                              <TableCell className={OVERVIEW_NUMERIC_CELL_CLASS}>
                                {holding.weight
                                  ? Number(holding.weight).toFixed(2)
                                  : "0.00"}
                                %
                              </TableCell>
                              <TableCell className={OVERVIEW_NUMERIC_CELL_CLASS}>
                                <span
                                  className={
                                    holding.drift && holding.drift > 0.01
                                      ? "text-red-600 dark:text-red-400"
                                      : holding.drift && holding.drift < -0.01
                                        ? "text-green-600 dark:text-green-400"
                                        : ""
                                  }
                                >
                                  {holding.drift
                                    ? (holding.drift > 0 ? "+" : "") +
                                      Number(holding.drift).toFixed(2)
                                    : "0.00"}
                                  %
                                </span>
                              </TableCell>
                            </>
                          )}

                        {/* Model portfolios: Show both model and actual weights + drift */}
                        {holdingInputType === "weight_based_model" &&
                          portfolioClassification === "model" && (
                            <>
                              <TableCell className={OVERVIEW_NUMERIC_CELL_CLASS}>
                                {enableLookthrough &&
                                holding.effective_real_weight !== undefined
                                  ? Number(
                                      holding.effective_real_weight
                                    ).toFixed(2)
                                  : holding.real_weight
                                    ? Number(holding.real_weight).toFixed(2)
                                    : "0.00"}
                                %
                              </TableCell>

                              <TableCell className={OVERVIEW_NUMERIC_CELL_CLASS}>
                                {enableLookthrough &&
                                holding.effective_weight !== undefined
                                  ? Number(holding.effective_weight).toFixed(2)
                                  : holding.weight
                                    ? Number(holding.weight).toFixed(2)
                                    : "0.00"}
                                %
                              </TableCell>

                              <TableCell className={OVERVIEW_NUMERIC_CELL_CLASS}>
                                <span
                                  className={
                                    holding.drift && holding.drift > 0.01
                                      ? "text-emerald-500 dark:text-emerald-400"
                                      : holding.drift && holding.drift < -0.01
                                        ? "text-red-500 dark:text-red-400"
                                        : ""
                                  }
                                >
                                  {holding.drift
                                    ? (holding.drift > 0 ? "+" : "") +
                                      Number(holding.drift).toFixed(2)
                                    : "0.00"}
                                  %
                                </span>
                              </TableCell>
                            </>
                          )}

                        {/* Actual portfolios: Show only current weight (when NOT in target comparison mode) */}
                        {portfolioClassification === "actual" &&
                          !showTargetComparison && (
                            <TableCell className={OVERVIEW_NUMERIC_CELL_CLASS}>
                              {enableLookthrough &&
                              holding.effective_weight !== undefined
                                ? Number(holding.effective_weight).toFixed(2)
                                : holding.weight
                                  ? Number(holding.weight).toFixed(2)
                                  : "0.00"}
                              %
                            </TableCell>
                          )}
                      </TableRow>
                    );
                    })}
                  </TableBody>
                  {totals && (
                    <tfoot>
                      <TableRow className="bg-gray-100 dark:bg-gray-800 font-semibold border-t-2 border-gray-300 dark:border-gray-600">
                        <TableCell className={`${OVERVIEW_HOLDING_CELL_CLASS} text-gray-900 dark:text-gray-100 font-semibold`}>
                          Total
                        </TableCell>

                        {/* Show column spacers for units/value columns if displayed (NOT in target comparison mode) */}
                        {portfolioClassification === "actual" &&
                          !showTargetComparison && (
                            <>
                              <TableCell className={OVERVIEW_FOOTER_CELL_CLASS}>
                                -
                              </TableCell>
                              <TableCell className={OVERVIEW_FOOTER_CELL_CLASS}>
                                -
                              </TableCell>
                              <TableCell className={OVERVIEW_FOOTER_CELL_CLASS}>
                                -
                              </TableCell>
                              <TableCell className={OVERVIEW_FOOTER_CELL_CLASS}>
                                {totals?.portfolio_value
                                  ? totals.portfolio_value.toLocaleString(
                                      undefined,
                                      { maximumFractionDigits: 0 }
                                    )
                                  : "0"}
                              </TableCell>
                            </>
                          )}

                        {/* Target comparison totals - show when in target comparison mode */}
                        {portfolioClassification === "actual" &&
                          showTargetComparison && (
                            <>
                              <TableCell className={OVERVIEW_FOOTER_CELL_CLASS}>
                                {totals?.real_weight
                                  ? Number(totals.real_weight).toFixed(2)
                                  : "0.00"}
                                %
                              </TableCell>
                              <TableCell className={OVERVIEW_FOOTER_CELL_CLASS}>
                                {totals?.weight
                                  ? Number(totals.weight).toFixed(2)
                                  : "0.00"}
                                %
                              </TableCell>
                              <TableCell className={OVERVIEW_FOOTER_CELL_CLASS}>
                                {totals?.drift
                                  ? (totals.drift > 0 ? "+" : "") +
                                    Number(totals.drift).toFixed(2)
                                  : "0.00"}
                                %
                              </TableCell>
                            </>
                          )}

                        {/* Model portfolios: Show totals for both model and actual weights + drift */}
                        {holdingInputType === "weight_based_model" &&
                          portfolioClassification === "model" && (
                            <>
                              <TableCell className={OVERVIEW_FOOTER_CELL_CLASS}>
                                {enableLookthrough &&
                                totals.effective_real_weight !== undefined
                                  ? Number(
                                      totals.effective_real_weight
                                    ).toFixed(2)
                                  : totals.real_weight
                                    ? Number(totals.real_weight).toFixed(2)
                                    : "0.00"}
                                %
                              </TableCell>
                              <TableCell className={OVERVIEW_FOOTER_CELL_CLASS}>
                                {enableLookthrough &&
                                totals.effective_weight !== undefined
                                  ? Number(totals.effective_weight).toFixed(2)
                                  : totals.weight
                                    ? Number(totals.weight).toFixed(2)
                                    : "0.00"}
                                %
                              </TableCell>
                              <TableCell className={OVERVIEW_NUMERIC_CELL_CLASS}>
                                <span
                                  className={
                                    totals.drift && totals.drift > 0.01
                                      ? "text-emerald-500 dark:text-emerald-400"
                                      : totals.drift && totals.drift < -0.01
                                        ? "text-red-500 dark:text-red-400"
                                        : "text-gray-900 dark:text-gray-100"
                                  }
                                >
                                  {totals.drift
                                    ? (totals.drift > 0 ? "+" : "") +
                                      Number(totals.drift).toFixed(2)
                                    : "0.00"}
                                  %
                                </span>
                              </TableCell>
                            </>
                          )}

                        {/* Actual portfolios: Show only current weight total (when NOT in target comparison mode) */}
                        {portfolioClassification === "actual" &&
                          !showTargetComparison && (
                            <TableCell className={OVERVIEW_FOOTER_CELL_CLASS}>
                              {enableLookthrough &&
                              totals.effective_weight !== undefined
                                ? Number(totals.effective_weight).toFixed(2)
                                : totals.weight
                                  ? Number(totals.weight).toFixed(2)
                                  : "0.00"}
                              %
                            </TableCell>
                          )}
                      </TableRow>
                    </tfoot>
                  )}
                </Table>
              </div>
            </>
          ) : (
            <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
              No holdings data available
            </p>
          )}
        </div>
      </div>

      {/* Trade Reconciliation Drawer */}
      <TradeReconciliationDrawer
        isOpen={tradeDrawerOpen}
        onClose={() => {
          setTradeDrawerOpen(false);
          setSelectedTradeHolding(null);
        }}
        portfolioId={entityId}
        resourceId={selectedTradeHolding?.resourceId || ""}
        resourceName={selectedTradeHolding?.resourceName || ""}
      />
    </div>
  );
};

export const Portfolio = forwardRef<
  { handleDrawerAnimationComplete: () => void },
  PortfolioComponentProps
>(
  (
    {
      id,
      initialActiveTab = TABS.OVERVIEW,
      onPlaygroundClick,
      showHeader = true,
      skipUrlNavigation = false,
    },
    ref
  ) => {
    // Initialize state with reducer
    const initialState: PortfolioState = {
      portfolio: null,
      metadata: null,
      loading: true,
      error: null,
      refreshCounter: 0,
      hasNoSnapshots: false,
    };

    const [state, dispatch] = useReducer(portfolioReducer, initialState);
    const [activeTab, setActiveTab] = useState<TabId>(initialActiveTab);
    const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
    const [isCreateDocumentDialogOpen, setIsCreateDocumentDialogOpen] =
      useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState<
      typeof BLANK_DOCUMENT_TEMPLATE | null
    >(null);
    const [availableTemplates, setAvailableTemplates] = useState<
      (typeof BLANK_DOCUMENT_TEMPLATE)[]
    >([]);
    const [snapshotData, setSnapshotData] = useState<LocalPortfolioType | null>(
      null
    );
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [pendingNavigation, setPendingNavigation] = useState<{
      url: string;
      state: any;
    } | null>(null);

    // Recalculation state
    const [showRecalculateDialog, setShowRecalculateDialog] = useState(false);

    // Add deletion state
    const [isDeleting, setIsDeleting] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [canDeletePortfolio, setCanDeletePortfolio] = useState<boolean | null>(null);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    // Access denied state (triggered by child components like holdings)
    const [childAccessDenied, setChildAccessDenied] = useState(false);

    // Notification system
    const { showSuccess, showError } = useNotification();
    const { openRefreshPreview } = useTaskProgressDialog();

    const location = useLocation();
    const navigate = useNavigate();
    const osActions = useOsActionsOptional();
    const { user } = useAuth();
    const setWindowTitle = useSetWindowTitle();

    // Update window title when portfolio data loads
    useEffect(() => {
      if (showHeader && state.portfolio?.fields?.metadata?.display_name) {
        setWindowTitle(state.portfolio.fields.metadata.display_name);
      }
    }, [showHeader, state.portfolio?.fields?.metadata?.display_name, setWindowTitle]);

    useEffect(() => {
      let cancelled = false;

      async function loadDeletePermission() {
        setCanDeletePortfolio(null);
        try {
          const permissions = await PortfoliosAPI.getPortfolioPermissions(id);
          if (!cancelled) {
            setCanDeletePortfolio(permissions.can_delete);
          }
        } catch (error) {
          console.error("Failed to load portfolio delete permission:", error);
          if (!cancelled) {
            setCanDeletePortfolio(false);
          }
        }
      }

      void loadDeletePermission();
      return () => {
        cancelled = true;
      };
    }, [id]);

    // Refs for child components
    const documentsListRef = useRef<DocumentsListRef>(null);

    // Refs to track component lifecycle and prevent duplicate requests
    const isMounted = useRef(true);
    const isLoadingRef = useRef(false);

    // Helper to navigate to screener in OS mode or classic mode
    const navigateToScreener = useCallback(() => {
      if (osActions) {
        osActions.openWindow({
          appId: "screener",
          title: "Screener",
          params: {},
        });
      } else {
        navigate("/screener");
      }
    }, [osActions, navigate]);

    // Expose the handleDrawerAnimationComplete method via ref
    useImperativeHandle(ref, () => ({
      handleDrawerAnimationComplete: () => {
        if (pendingNavigation) {
          navigate(pendingNavigation.url, pendingNavigation.state);
          setPendingNavigation(null);
        }
      },
    }));

    // Function to load portfolio data
    const loadPortfolio = useCallback(async () => {
      if (isLoadingRef.current || !isMounted.current) return;

      isLoadingRef.current = true;
      dispatch({ type: "FETCH_START" });

      try {
        // Fetch both snapshot data and resource metadata in parallel
        const [portfolioData, resourceMetadata] = await Promise.all([
          SnapshotsAPI.getSnapshotData({
            as_at: new Date().toISOString() as any,
            resources: [
              {
                resource_type: "portfolio",
                resource_id: id as any,
              },
            ],
            fields: [
              "metadata.display_name",
              "fundamental_data.base_currency",
              "fundamental_data.description",
              "exposures.detailed_asset_allocation.primary",
              "exposures.combined_standardized_regions.primary",
            ],
          }),
          // Screener metadata can 403 while snapshots succeed (e.g. screener drawer deep links).
          // Do not fail the whole page — mirror PortfolioLoader / Product patterns.
          ScreenerAPI.fetchResourceMetadata("portfolio", id).catch(() => null),
        ]);

        if (isMounted.current) {
          // Handle null response: portfolio exists but has no snapshots yet
          if (portfolioData === null) {
            const metadata = {
              resource_display_name:
                resourceMetadata?.resource_display_name || id,
              resource_id: resourceMetadata?.resource_id || id,
              resource_type: resourceMetadata?.resource_type || "portfolio",
              stream_type: resourceMetadata?.stream_type || "default",
              ...(resourceMetadata?.benchmark_resource_id && {
                benchmark_resource_id: resourceMetadata?.benchmark_resource_id,
                benchmark_display_name:
                  resourceMetadata?.benchmark_display_name,
                benchmark_resource_type:
                  resourceMetadata?.benchmark_resource_type,
                benchmark_stream_type:
                  resourceMetadata?.benchmark_stream_type || "default",
              }),
            };

            dispatch({
              type: "FETCH_NO_SNAPSHOTS",
              payload: { metadata },
            });
            return;
          }

          if (
            portfolioData &&
            typeof portfolioData === "object" &&
            "data" in portfolioData
          ) {
            const data = portfolioData.data;
            const portfolioKeys = Object.keys(data);
            const exactKey = `portfolio:${id}`;

            // Handle empty data object (portfolio exists but no snapshot data)
            if (portfolioKeys.length === 0) {
              const metadata = {
                resource_display_name:
                  resourceMetadata?.resource_display_name || id,
                resource_id: resourceMetadata?.resource_id || id,
                resource_type: resourceMetadata?.resource_type || "portfolio",
                stream_type: resourceMetadata?.stream_type || "default",
                ...(resourceMetadata?.benchmark_resource_id && {
                  benchmark_resource_id: resourceMetadata?.benchmark_resource_id,
                  benchmark_display_name:
                    resourceMetadata?.benchmark_display_name,
                  benchmark_resource_type:
                    resourceMetadata?.benchmark_resource_type,
                  benchmark_stream_type:
                    resourceMetadata?.benchmark_stream_type || "default",
                }),
              };

              dispatch({
                type: "FETCH_NO_SNAPSHOTS",
                payload: { metadata },
              });
              return;
            }

            let portfolioInfo = null;
            if (data[exactKey]) {
              portfolioInfo = data[exactKey] as LocalPortfolioType;
            } else {
              const matchingKey = portfolioKeys.find((key) => key.includes(id));
              if (matchingKey) {
                portfolioInfo = data[matchingKey] as LocalPortfolioType;
              }
            }

            if (portfolioInfo) {
              setSnapshotData(portfolioInfo);
              // Extract metadata directly from resourceMetadata (flat structure)
              const metadata = {
                resource_display_name:
                  resourceMetadata?.resource_display_name ||
                  portfolioInfo.fields.metadata.display_name,
                resource_id: resourceMetadata?.resource_id || id,
                resource_type: resourceMetadata?.resource_type || "portfolio",
                stream_type: resourceMetadata?.stream_type || "default",
                // Add benchmark info if available
                ...(resourceMetadata?.benchmark_resource_id && {
                  benchmark_resource_id: resourceMetadata?.benchmark_resource_id,
                  benchmark_display_name:
                    resourceMetadata?.benchmark_display_name,
                  benchmark_resource_type:
                    resourceMetadata?.benchmark_resource_type,
                  benchmark_stream_type:
                    resourceMetadata?.benchmark_stream_type || "default",
                }),
              };

              dispatch({
                type: "FETCH_SUCCESS",
                payload: {
                  portfolio: portfolioInfo,
                  metadata,
                },
              });
            } else {
              // Portfolio key not found in data - treat as no snapshots
              const metadata = {
                resource_display_name:
                  resourceMetadata?.resource_display_name || id,
                resource_id: resourceMetadata?.resource_id || id,
                resource_type: resourceMetadata?.resource_type || "portfolio",
                stream_type: resourceMetadata?.stream_type || "default",
                ...(resourceMetadata?.benchmark_resource_id && {
                  benchmark_resource_id: resourceMetadata?.benchmark_resource_id,
                  benchmark_display_name:
                    resourceMetadata?.benchmark_display_name,
                  benchmark_resource_type:
                    resourceMetadata?.benchmark_resource_type,
                  benchmark_stream_type:
                    resourceMetadata?.benchmark_stream_type || "default",
                }),
              };

              dispatch({
                type: "FETCH_NO_SNAPSHOTS",
                payload: { metadata },
              });
            }
          } else if (portfolioData) {
            try {
              if ("metadata" in portfolioData && "fields" in portfolioData) {
                const portfolioInfo =
                  portfolioData as unknown as LocalPortfolioType;
                setSnapshotData(portfolioInfo);

                // Extract metadata directly from resourceMetadata (flat structure)
                const metadata = {
                  resource_display_name:
                    resourceMetadata?.resource_display_name ||
                    portfolioInfo.fields.metadata.display_name,
                  resource_id: resourceMetadata?.resource_id || id,
                  resource_type: resourceMetadata?.resource_type || "portfolio",
                  stream_type: resourceMetadata?.stream_type || "default",
                  // Add benchmark info if available
                  ...(resourceMetadata?.benchmark_resource_id && {
                    benchmark_resource_id:
                      resourceMetadata?.benchmark_resource_id,
                    benchmark_display_name:
                      resourceMetadata?.benchmark_display_name,
                    benchmark_resource_type:
                      resourceMetadata?.benchmark_resource_type,
                    benchmark_stream_type:
                      resourceMetadata?.benchmark_stream_type || "default",
                  }),
                };

                dispatch({
                  type: "FETCH_SUCCESS",
                  payload: {
                    portfolio: portfolioInfo,
                    metadata,
                  },
                });
              } else {
                const possibleKeys = Object.keys(portfolioData);
                const matchingKey = possibleKeys.find(
                  (key) => key.includes(id) || key.includes("portfolio")
                );
                if (
                  matchingKey &&
                  portfolioData[matchingKey] &&
                  "metadata" in portfolioData[matchingKey] &&
                  "fields" in portfolioData[matchingKey]
                ) {
                  const portfolioInfo = portfolioData[
                    matchingKey
                  ] as LocalPortfolioType;
                  setSnapshotData(portfolioInfo);

                  // Extract metadata directly from resourceMetadata (flat structure)
                  const metadata = {
                    resource_display_name:
                      resourceMetadata?.resource_display_name ||
                      portfolioInfo.fields.metadata.display_name,
                    resource_id: resourceMetadata?.resource_id || id,
                    resource_type:
                      resourceMetadata?.resource_type || "portfolio",
                    stream_type: resourceMetadata?.stream_type || "default",
                    // Add benchmark info if available
                    ...(resourceMetadata?.benchmark_resource_id && {
                      benchmark_resource_id:
                        resourceMetadata?.benchmark_resource_id,
                      benchmark_display_name:
                        resourceMetadata?.benchmark_display_name,
                      benchmark_resource_type:
                        resourceMetadata?.benchmark_resource_type,
                      benchmark_stream_type:
                        resourceMetadata?.benchmark_stream_type || "default",
                    }),
                  };

                  dispatch({
                    type: "FETCH_SUCCESS",
                    payload: {
                      portfolio: portfolioInfo,
                      metadata,
                    },
                  });
                } else {
                  // Portfolio data structure not recognized - treat as no snapshots
                  const metadata = {
                    resource_display_name:
                      resourceMetadata?.resource_display_name || id,
                    resource_id: resourceMetadata?.resource_id || id,
                    resource_type: resourceMetadata?.resource_type || "portfolio",
                    stream_type: resourceMetadata?.stream_type || "default",
                    ...(resourceMetadata?.benchmark_resource_id && {
                      benchmark_resource_id:
                        resourceMetadata?.benchmark_resource_id,
                      benchmark_display_name:
                        resourceMetadata?.benchmark_display_name,
                      benchmark_resource_type:
                        resourceMetadata?.benchmark_resource_type,
                      benchmark_stream_type:
                        resourceMetadata?.benchmark_stream_type || "default",
                    }),
                  };

                  dispatch({
                    type: "FETCH_NO_SNAPSHOTS",
                    payload: { metadata },
                  });
                }
              }
            } catch (innerError) {
              console.error("Error parsing portfolio data:", innerError);
              // On parsing error, treat as no snapshots available
              const metadata = {
                resource_display_name:
                  resourceMetadata?.resource_display_name || id,
                resource_id: resourceMetadata?.resource_id || id,
                resource_type: resourceMetadata?.resource_type || "portfolio",
                stream_type: resourceMetadata?.stream_type || "default",
                ...(resourceMetadata?.benchmark_resource_id && {
                  benchmark_resource_id: resourceMetadata?.benchmark_resource_id,
                  benchmark_display_name:
                    resourceMetadata?.benchmark_display_name,
                  benchmark_resource_type:
                    resourceMetadata?.benchmark_resource_type,
                  benchmark_stream_type:
                    resourceMetadata?.benchmark_stream_type || "default",
                }),
              };

              dispatch({
                type: "FETCH_NO_SNAPSHOTS",
                payload: { metadata },
              });
            }
          } else {
            // No portfolioData returned - treat as no snapshots
            const metadata = {
              resource_display_name:
                resourceMetadata?.resource_display_name || id,
              resource_id: resourceMetadata?.resource_id || id,
              resource_type: resourceMetadata?.resource_type || "portfolio",
              stream_type: resourceMetadata?.stream_type || "default",
              ...(resourceMetadata?.benchmark_resource_id && {
                benchmark_resource_id: resourceMetadata?.benchmark_resource_id,
                benchmark_display_name: resourceMetadata?.benchmark_display_name,
                benchmark_resource_type:
                  resourceMetadata?.benchmark_resource_type,
                benchmark_stream_type:
                  resourceMetadata?.benchmark_stream_type || "default",
              }),
            };

            dispatch({
              type: "FETCH_NO_SNAPSHOTS",
              payload: { metadata },
            });
          }
        }
      } catch (err: any) {
        console.error("Error loading portfolio:", err);
        if (isMounted.current) {
          // Check for 403 access denied errors using unified utility
          const { isAccessDenied } = isAccessDeniedError(err);

          if (isAccessDenied) {
            dispatch({ type: "FETCH_ERROR", payload: ACCESS_DENIED_ERROR });
          } else {
            const errorMessage = err instanceof Error ? err.message : String(err);
            dispatch({ type: "FETCH_ERROR", payload: errorMessage || "Failed to load portfolio data" });
          }
        }
      } finally {
        isLoadingRef.current = false;
      }
    }, [id]);

    // Cleanup on unmount
    useEffect(() => {
      isMounted.current = true;
      return () => {
        isMounted.current = false;
      };
    }, []);

    // Effect to handle initial load and refreshes
    useEffect(() => {
      loadPortfolio();
    }, [loadPortfolio, state.refreshCounter]);

    // Parse the tab from the URL on component mount (skip when in drawer mode)
    useEffect(() => {
      if (skipUrlNavigation) return;
      const searchParams = new URLSearchParams(location.search);
      const tab = searchParams.get("tab") as TabId | null;
      if (tab && Object.values(TABS).includes(tab)) {
        setActiveTab(tab);
      }
    }, [location.search, skipUrlNavigation]);

    // Update URL when tab changes (skip URL navigation when in drawer mode)
    const handleTabChange = (tabId: string) => {
      if (Object.values(TABS).includes(tabId as TabId)) {
        setActiveTab(tabId as TabId);

        // Skip URL navigation when rendered in a drawer/modal
        if (skipUrlNavigation) return;

        const newSearchParams = new URLSearchParams(location.search);

        // Always clear existing playground-specific params before setting new ones or switching to a non-playground tab
        PLAYGROUND_URL_PARAMS_TO_MANAGE.forEach((key) =>
          newSearchParams.delete(key)
        );

        newSearchParams.set("tab", tabId); // Set the current tab

        if (tabId === TABS.PLAYGROUND && state.metadata) {
          // formatPlaygroundUrl is expected to be in scope (imported from "@/utils/playground")
          const { url: fullPlaygroundUrl } = formatPlaygroundUrl(
            state.metadata
          );
          const playgroundQueryParams =
            extractPlaygroundQueryParams(fullPlaygroundUrl);

          for (const [key, value] of Object.entries(playgroundQueryParams)) {
            newSearchParams.set(key, value);
          }
        }
        // For other tabs, playground-specific params have already been cleared.

        navigate(`${location.pathname}?${newSearchParams.toString()}`, {
          replace: true,
        });
      }
    };

    // Handle document upload dialog
    const handleOpenUploadDialog = () => {
      setIsUploadDialogOpen(true);
    };

    const handleCloseUploadDialog = () => {
      setIsUploadDialogOpen(false);
    };

    // Effect to filter templates based on company ID from auth context
    useEffect(() => {
      const companyId = user?.company_id || null;
      const filtered = documentTemplates.filter(
        (template) =>
          template.companyId === null || template.companyId === companyId
      );
      setAvailableTemplates(filtered);
    }, [user?.company_id]);

    // Handle document creation dialog
    const handleCreateDocumentDialog = (templateId?: string) => {
      const template = templateId
        ? documentTemplates.find((t) => t.id === templateId) ||
          BLANK_DOCUMENT_TEMPLATE
        : BLANK_DOCUMENT_TEMPLATE;

      setSelectedTemplate(template);
      setIsCreateDocumentDialogOpen(true);
    };

    const handleCloseCreateDocumentDialog = () => {
      setIsCreateDocumentDialogOpen(false);
      setSelectedTemplate(null);
    };

    // Create a function to upload portfolio documents
    const uploadToPortfolio = (file: File) => {
      return DocumentsAPI.uploadDocument(file, {
        resource_type: "portfolio",
        resource_id: id,
        process_in_background: false,
      });
    };

    const handleUploadComplete = () => {
      // Close the dialog
      setIsUploadDialogOpen(false);

      // Refresh the documents list if we're on the documents tab
      if (activeTab === TABS.DOCUMENTS && documentsListRef.current) {
        documentsListRef.current.refreshDocuments();
      }
    };

    // Handle playground click (only used in drawer mode)
    const handlePlaygroundClick = useCallback(
      (e?: React.MouseEvent) => {
        e?.preventDefault();

        // Format the navigation info for drawer mode
        if (state.metadata) {
          const { url, state: navigationState } = formatPlaygroundUrl(
            state.metadata
          );

          // In drawer mode, store navigation info and let parent handle drawer close
          setPendingNavigation({
            url,
            state: navigationState,
          });

          // Call onPlaygroundClick to trigger drawer close and navigation
          if (onPlaygroundClick) {
            onPlaygroundClick(
              e || ({ preventDefault: () => {} } as React.MouseEvent)
            );
          }
        }
      },
      [id, onPlaygroundClick, state.metadata, setPendingNavigation]
    );

    // Handle portfolio recalculation
    const handleRecalculate = () => {
      if (!state.portfolio) return;
      setShowRecalculateDialog(true);
    };

    const confirmRecalculate = async () => {
      if (!state.portfolio) return;

      setShowRecalculateDialog(false);

      openRefreshPreview({
        resource_ids: [id],
        recalculate_portfolio_ids: [id],
        title: "Portfolio recalculation",
      });
    };

    // Handle portfolio deletion
    const deleteDisabled = isDeleting || canDeletePortfolio !== true;
    const deleteTooltip = isDeleting
      ? "Portfolio deletion in progress..."
      : canDeletePortfolio === null
        ? "Checking delete permission..."
        : canDeletePortfolio === false
          ? "You don't have permission to delete this portfolio. Contact your administrator if you need access."
          : "Delete this portfolio permanently";

    const handleDelete = () => {
      if (!state.portfolio || deleteDisabled || canDeletePortfolio !== true) return;
      setDeleteError(null);
      setShowDeleteDialog(true);
    };

    const confirmDelete = async () => {
      if (!state.portfolio || deleteDisabled || canDeletePortfolio !== true) return;

      setIsDeleting(true);
      setDeleteError(null);

      try {
        await PortfoliosAPI.deletePortfolio(id);

        setShowDeleteDialog(false);
        showSuccess(
          "Portfolio Deleted",
          `${state.portfolio.fields.metadata.display_name} has been permanently deleted.`,
          { duration: 5000 }
        );

        setTimeout(() => {
          navigateToScreener();
        }, 2000);
      } catch (error: unknown) {
        console.error("Portfolio deletion failed:", error);
        const message = getErrorMessage(error, "Failed to delete portfolio");
        setDeleteError(message);
        showError("Portfolio Deletion Failed", message, { duration: 8000, autoClose: true });
      } finally {
        setIsDeleting(false);
      }
    };

    const playgroundAssets = useMemo(
      () =>
        state.metadata
          ? buildPlaygroundAssetsFromMetadata(state.metadata)
          : null,
      [state.metadata]
    );

    // Memoize the tab content
    const tabContent = useMemo(
      () => [
        {
          id: TABS.OVERVIEW,
          label: "Overview",
          icon: <FontAwesomeIcon icon={faGrid2} />,
          content: state.portfolio && (
            <AniTrackableWidget
              id={`portfolio-overview-${id}`}
              type="table"
              name="Portfolio Overview"
              data={{
                portfolioName: state.portfolio.fields.metadata.display_name
              }}
            >
              <div className="w-full">
                <PortfolioHoldingsOnly
                  entity={state.portfolio}
                  entityId={id}
                  onAccessDenied={() => setChildAccessDenied(true)}
                />
              </div>
            </AniTrackableWidget>
          ),
        },

        {
          id: TABS.CONTRIBUTIONS,
          label: "Contributions",
          icon: <FontAwesomeIcon icon={faPieChart} />,
          content: state.portfolio && (
            <AniTrackableWidget
              id={`portfolio-contributions-${id}`}
              type="chart"
              name="Portfolio Contributions"
              data={{
                portfolioName: state.portfolio.fields.metadata.display_name
              }}
            >
              <div className="flex h-full flex-grow">
                <div className="w-full">
                  <PortfolioContributions portfolioId={id} />
                </div>
              </div>
            </AniTrackableWidget>
          ),
        },
        {
          id: TABS.XRAY,
          label: "X-Ray",
          icon: <FontAwesomeIcon icon={faMagnifyingGlassChart} />,
          content: state.portfolio && (
            <AniTrackableWidget
              id={`portfolio-xray-${id}`}
              type="chart"
              name="Portfolio X-Ray Analysis"
              data={{
                portfolioName: state.portfolio.fields.metadata.display_name
              }}
            >
              <div className="flex h-full flex-grow">
                <div className="w-full">
                  <PortfolioXRay portfolioId={id} />
                </div>
              </div>
            </AniTrackableWidget>
          ),
        },
        {
          id: TABS.PLAYGROUND,
          label: "Playground",
          icon: <FontAwesomeIcon icon={faChartMixed} />,
          content: state.portfolio && (
            <AniTrackableWidget
              id={`portfolio-playground-${id}`}
              type="chart"
              name="Portfolio Playground"
              className="h-full flex flex-col min-h-0"
              data={{
                portfolioName: state.portfolio.fields.metadata.display_name
              }}
            >
              <div className="flex h-full flex-grow ">
                <div className="w-full">
                  <DashboardProvider>
                    <PlaygroundProvider>
                      <Playground
                        initialAssets={
                          playgroundAssets?.initialAssets ?? [id]
                        }
                        assetDisplayNames={
                          playgroundAssets?.initialDisplayNames ?? {
                            [id]: state.portfolio.fields.metadata.display_name,
                          }
                        }
                        initialAssetMetadata={
                          playgroundAssets?.initialAssetMetadata ?? {}
                        }
                        showAssetSearch={true}
                        allowAssetToggle={true}
                        hideTabs={true}
                        isSearchOpen={isSearchOpen}
                        setIsSearchOpen={setIsSearchOpen}
                        defaultResourceType="portfolio"
                      />
                    </PlaygroundProvider>
                  </DashboardProvider>
                </div>
              </div>
            </AniTrackableWidget>
          ),
          // Only add onClick handler when in drawer mode
          ...(onPlaygroundClick && { onClick: handlePlaygroundClick }),
        },
        {
          id: TABS.DOCUMENTS,
          label: "Documents",
          icon: <FontAwesomeIcon icon={faFolder} />,
          content: state.portfolio && (
            <AniTrackableWidget
              id={`portfolio-documents-${id}`}
              type="list"
              name="Portfolio Documents"
              data={{
                portfolioName: state.portfolio.fields.metadata.display_name
              }}
            >
              <div className="flex h-full flex-grow">
                <div className="p-4 w-full">
                  <DocumentsList
                    ref={documentsListRef}
                    entityId={id}
                    entityType="portfolio"
                    title="Portfolio Documents"
                    fetchDocuments={fetchPortfolioDocuments}
                    hideSuggestedFilters={true}
                  />
                </div>
              </div>
            </AniTrackableWidget>
          ),
        },
        {
          id: TABS.REPORTS,
          label: "Reports",
          icon: <FontAwesomeIcon icon={faFileLines} />,
          content: state.portfolio && (
            <AniTrackableWidget
              id={`portfolio-reports-${id}`}
              type="list"
              name="Portfolio Reports"
              data={{
                portfolioName: state.portfolio.fields.metadata.display_name
              }}
            >
              <div className="flex h-full flex-grow">
                <div className="w-full">
                  <PortfolioReports
                    entityId={id}
                    entityType="portfolio"
                    entityName={state.portfolio.fields.metadata.display_name}
                  />
                </div>
              </div>
            </AniTrackableWidget>
          ),
        },
        {
          id: TABS.HOLDINGS,
          label: "Historical",
          icon: <FontAwesomeIcon icon={faList} />,
          content: state.portfolio && (
            <AniTrackableWidget
              id={`portfolio-historical-${id}`}
              type="table"
              name="Historical Holdings"
              data={{
                portfolioName: state.portfolio.fields.metadata.display_name
              }}
            >
              <div className="flex h-full flex-grow">
                <div className="w-full">
                  <PortfolioHoldings
                    portfolioId={id}
                    onRecalculationTriggered={() => {
                      openRefreshPreview({
                        resource_ids: [id],
                        recalculate_portfolio_ids: [id],
                        title: "Portfolio recalculation",
                      });
                      dispatch({ type: "REFRESH" });
                    }}
                  />
                </div>
              </div>
            </AniTrackableWidget>
          ),
        },
        {
          id: TABS.FIELD_MANAGEMENT,
          label: "Fields",
          icon: <FontAwesomeIcon icon={faList} />,
          content: state.portfolio && (
            <div className="flex h-full flex-grow">
              <div className="p-4 w-full">
                <PortfolioFieldManagement portfolioId={id} />
              </div>
            </div>
          ),
        },
      ],
      [
        id,
        state.portfolio,
        playgroundAssets,
        isSearchOpen,
        handlePlaygroundClick,
        onPlaygroundClick,
      ]
    );

    if (state.loading) {
      return <div className="p-8 text-gray-900 dark:text-gray-100">Loading...</div>;
    }

    // Show "no snapshots" UI when portfolio exists but hasn't been processed yet
    if (state.hasNoSnapshots) {
      const handleRecalculateNoSnapshots = () => {
        openRefreshPreview({
          resource_ids: [id],
          recalculate_portfolio_ids: [id],
          title: "Portfolio recalculation",
        });
      };

      return (
        <div className="h-full flex items-center justify-center p-8 min-h-[400px]">
          <div className="text-center max-w-lg">
            <div className="mx-auto w-20 h-20 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center mb-6">
              <FontAwesomeIcon
                icon={faChartLineUp}
                className="text-indigo-600 dark:text-indigo-400 text-3xl"
              />
            </div>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-3">
              Portfolio Created Successfully
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Your portfolio is being processed. Performance data and snapshots
              will be available after the next data refresh.
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500 mb-6">
              You can trigger a recalculation now or wait for the next scheduled
              data refresh.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Button onClick={handleRecalculateNoSnapshots} color="indigo">
                <FontAwesomeIcon
                  icon={faArrowsRotate}
                  className="w-4 h-4 mr-2"
                />
                Recalculate Now
              </Button>
              <Button onClick={() => dispatch({ type: "REFRESH" })} color="white">
                Check Again
              </Button>
              <Button onClick={navigateToScreener} color="white">
                View All Portfolios
              </Button>
            </div>
          </div>
        </div>
      );
    }

    // Show Access Denied UI for 403 errors (from initial fetch or child components)
    if (state.error === ACCESS_DENIED_ERROR || childAccessDenied) {
      return (
        <div className="h-full flex items-center justify-center p-8 min-h-[400px]">
          <div className="text-center max-w-md">
          <div className="mx-auto w-20 h-20 bg-slate-800 rounded-full flex items-center justify-center mb-6">
            <FontAwesomeIcon icon={faUserLock} className="text-white text-3xl" />

            </div>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-3">
              Access Denied
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              You don't have permission to view this portfolio. Please contact your administrator if you believe this is an error.
            </p>
            <div className="flex gap-3 justify-center">
              <Button
                onClick={() => navigate(-1)}
                color="color"
              >
                <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Go Back
              </Button>
              <Button
                onClick={navigateToScreener}
                color="indigo"
              >
                View All Portfolios
              </Button>
            </div>
          </div>
        </div>
      );
    }

    if (state.error || !state.portfolio) {
      return (
        <div className="p-8 text-gray-900 dark:text-gray-100">Error: {state.error || "Portfolio not found"}</div>
      );
    }

    return (
      <>
        <PageLayout
          title={state.portfolio.fields.metadata.display_name || "Portfolio"}
          staticTabs={true}
          aniNav
          headerActionsComponent={
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleRecalculate}
                disabled={isDeleting}
                className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition-colors hover:bg-gray-100 dark:hover:bg-gray-700 ${
                  isDeleting
                    ? "cursor-not-allowed opacity-50"
                    : "cursor-pointer"
                }`}
                title="Recalculate portfolio data from scratch"
                aria-label="Recalculate portfolio data from scratch"
              >
                <FontAwesomeIcon
                  icon={faArrowsRotate}
                  className="h-4 w-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                />
              </button>
              <Tooltip content={deleteTooltip} placement="bottom">
                <span className="inline-flex">
                  <DestructiveTrashIconButton
                    onClick={handleDelete}
                    disabled={deleteDisabled}
                    title={deleteTooltip}
                    aria-label={deleteTooltip}
                  />
                </span>
              </Tooltip>
            </div>
          }
          tabs={tabContent}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          disableIconEdit={true}
          backLink={{
            to: "/screener",
            label: "Back",
          }}
          hideHeader={!showHeader}
          primaryAction={{
            label: "Upload Document",
            onClick: handleOpenUploadDialog,
            icon: <FontAwesomeIcon icon={faUpload} />,
            dropdownItems: [
              {
                name: "Create Blank Document",
                onClick: () => handleCreateDocumentDialog(),
              },
              ...availableTemplates
                .filter(
                  (template) => template.id !== BLANK_DOCUMENT_TEMPLATE.id
                )
                .map((template) => ({
                  name: template.name,
                  onClick: () => handleCreateDocumentDialog(template.id),
                })),
            ],
          }}
        />

        {/* Recalculation Confirmation Dialog */}
        <Dialog
          open={showRecalculateDialog}
          onClose={() => setShowRecalculateDialog(false)}
          size="lg"
        >
          <DialogTitle>
            <div className="flex items-center gap-2">
              Recalculate Portfolio Data
            </div>
          </DialogTitle>
          <DialogBody>
            <div className="space-y-4">
              <p className="text-gray-700 dark:text-gray-300">
                Are you sure you want to recalculate the portfolio{" "}
                <span className="font-medium text-white">
                  "{state.portfolio?.fields.metadata.display_name}"
                </span>
                ?
              </p>

              <div className="border-l-4 border-yellow-400   p-4 py-0">
                <h4 className=" mb-2">This process will:</h4>
                <ul className="  space-y-1">
                  <li>
                    • Delete all derived data (snapshots, returns, timeseries)
                  </li>
                  <li>• Preserve base portfolio data and manual holdings</li>
                  <li>• Recalculate everything from scratch</li>
                </ul>
              </div>

              <div className="font-mono bg-slate-800 rounded-lg p-4">
                <div className="flex items-center gap-2">
                  <svg
                    className="h-5 w-5 text-slate-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
                    Estimated Duration: 60-90 seconds
                  </span>
                </div>
              </div>
            </div>
          </DialogBody>
          <DialogActions>
            <Button onClick={() => setShowRecalculateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={confirmRecalculate} color="indigo">
              Recalculate Portfolio
            </Button>
          </DialogActions>
        </Dialog>

        {/* Portfolio Deletion Confirmation Dialog */}
        <Dialog
          open={showDeleteDialog}
          onClose={() => {
            if (isDeleting) return;
            setShowDeleteDialog(false);
            setDeleteError(null);
          }}
          size="lg"
        >
          <DialogTitle>
            <div className="flex items-center gap-2">
              <FontAwesomeIcon
                icon={faTrashAlt}
                className="h-5 w-5 text-red-500"
              />
              Delete Portfolio
            </div>
          </DialogTitle>
          <DialogBody>
            <div className="space-y-4">
              {deleteError ? (
                <div
                  role="alert"
                  className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300"
                >
                  {deleteError}
                </div>
              ) : null}
              <p className="text-gray-700 dark:text-gray-300">
                Are you sure you want to permanently delete the portfolio{" "}
                <span className="font-semibold">
                  "{state.portfolio?.fields.metadata.display_name}"
                </span>
                ?
              </p>

              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <h4 className="font-medium text-red-800 dark:text-red-200 mb-2">
                  ⚠️ This action is irreversible and will:
                </h4>
                <ul className="text-sm text-red-700 dark:text-red-300 space-y-1">
                  <li>• Delete the portfolio and all related data</li>
                  <li>• Remove all snapshots, returns, and timeseries data</li>
                  <li>• Delete all holdings and contributions data</li>
                  <li>• Remove initialization tracking records</li>
                </ul>
              </div>

              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                <div className="flex items-center gap-2">
                  <svg
                    className="h-5 w-5 text-amber-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-sm font-medium text-amber-800 dark:text-amber-200">
                    This cannot be undone
                  </span>
                </div>
              </div>
            </div>
          </DialogBody>
          <DialogActions>
            <Button
              onClick={() => {
                setShowDeleteDialog(false);
                setDeleteError(null);
              }}
              outline
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button onClick={confirmDelete} color="red" disabled={isDeleting}>
              <FontAwesomeIcon icon={faTrashAlt} className="mr-2" />
              Delete Portfolio
            </Button>
          </DialogActions>
        </Dialog>

        {/* Upload Dialog */}
        <UploadDialog
          isOpen={isUploadDialogOpen}
          onClose={handleCloseUploadDialog}
          title="Upload Portfolio Document"
          description={`Upload a document for ${state.portfolio.fields.metadata.display_name}`}
          uploadFunction={uploadToPortfolio}
          onUploadComplete={handleUploadComplete}
        />

        {/* Document Editor Drawer */}
        <DocumentEditorDrawer
          isOpen={isCreateDocumentDialogOpen}
          onClose={handleCloseCreateDocumentDialog}
          initialContent={selectedTemplate?.body}
          onSaveComplete={() => {
            handleCloseCreateDocumentDialog();
            if (documentsListRef.current) {
              documentsListRef.current.refreshDocuments();
            }
          }}
          resourceMetadata={{
            resource_type: "portfolio",
            resource_id: id,
          }}
        />
      </>
    );
  }
);
